import { AdminChapter, AdminChapterStatus, AdminLesson, LocalizedImageReference } from 'types/admin/modulesV2';
import { create } from 'zustand';

export type ChaptersData = { chapters: AdminChapter[]; localizedImages: LocalizedImageReference[] };
type State = ChaptersData & { initializing: boolean; loading: boolean };

type Actions = {
  setInitialChapters: (chapters: AdminChapter[], localizedImages: LocalizedImageReference[]) => void;
  addChapter: () => void;
  deleteChapter: (index: number) => void;
  updateChapterTitle: (index: number, title: string) => void;
  updateChapterStatus: (index: number, status: AdminChapterStatus) => void;
  addLesson: (index: number, newLessons: AdminLesson[], localizedImages: LocalizedImageReference[]) => void;
  updateLesson: (chapterIndex: number, lessonIndex: number, updatedLesson: AdminLesson, localizedImages: LocalizedImageReference[]) => void;
  deleteLesson: (chapterIndex: number, lessonIndex: number) => void;
  sortChapters: (from: number, to: number) => void;
  sortLessons: (chapterIndex: number, from: number, to: number) => void;
  reset: () => void;
  setLoadingInitialData: (initializing: boolean) => void;
  setLoading: (loading: boolean) => void;
};

export const useChaptersStore = create<State & Actions>((set, get, store) => ({
  chapters: [],
  localizedImages: [],
  initializing: false,
  loading: false,
  setInitialChapters: (chapters: AdminChapter[], localizedImages: LocalizedImageReference[]) =>
    set(() => ({ chapters, localizedImages })),
  addChapter: () => set((state: State) => ({ chapters: [...state.chapters, emptyChapter()] })),
  deleteChapter: (index: number) => set((state: State) => ({ chapters: state.chapters.filter((chapter, i) => i !== index) })),
  updateChapterTitle: (index: number, title: string) =>
    set((state: State) => ({ chapters: state.chapters.map((chapter, i) => (i !== index ? chapter : { ...chapter, title })) })),
  updateChapterStatus: (index: number, status: AdminChapterStatus) =>
    set((state: State) => ({ chapters: state.chapters.map((chapter, i) => (i !== index ? chapter : { ...chapter, status })) })),
  addLesson: (index: number, newLessons: AdminLesson[], localizedImages: LocalizedImageReference[]) =>
    set((state: State) => ({
      chapters: state.chapters.map((chapter, i) => (i !== index ? chapter : { ...chapter, lessons: [...chapter.lessons, ...newLessons] })),
      localizedImages: [...state.localizedImages, ...localizedImages],
    })),
  updateLesson: (chapterIndex: number, lessonIndex: number, updatedLesson: AdminLesson, localizedImages: LocalizedImageReference[]) =>
    set((state: State) => {
      const oldLesson = state.chapters[chapterIndex]?.lessons[lessonIndex];
      const oldMediaIds = new Set(oldLesson?.medias ?? []);
      return {
        chapters: state.chapters.map((chapter, i) =>
          i !== chapterIndex
            ? chapter
            : { ...chapter, lessons: chapter.lessons.map((lesson, i) => (i !== lessonIndex ? lesson : updatedLesson)) }
        ),
        localizedImages: [
          ...state.localizedImages.filter((ref) => !oldMediaIds.has(ref.sourceImageId)),
          ...localizedImages,
        ],
      };
    }),
  deleteLesson: (chapterIndex: number, lessonIndex: number) =>
    set((state: State) => {
      const removedMediaIds = new Set(state.chapters[chapterIndex]?.lessons[lessonIndex]?.medias ?? []);
      const localizedImages = removedMediaIds.size > 0
        ? state.localizedImages.filter((ref) => !removedMediaIds.has(ref.sourceImageId))
        : state.localizedImages;

      return {
        chapters: state.chapters.map((chapter, i) =>
          i !== chapterIndex ? chapter : { ...chapter, lessons: chapter.lessons.filter((lesson, index) => lessonIndex !== index) }
        ),
        localizedImages,
      };
    }),
  sortChapters: (from: number, to: number) => set((state: State) => ({ chapters: moveItems(state.chapters, from, to) as AdminChapter[] })),
  sortLessons: (chapterIndex: number, from: number, to: number) =>
    set((state: State) => ({
      chapters: state.chapters.map((chapter, index) =>
        index !== chapterIndex ? chapter : { ...chapter, lessons: moveItems(chapter.lessons, from, to) as AdminLesson[] }
      ),
    })),
  reset: () => set(store.getInitialState()),
  setLoadingInitialData: (initializing: boolean) => set(() => ({ initializing })),
  setLoading: (loading: boolean) => set(() => ({ loading })),
}));

const emptyChapter = (): AdminChapter => ({ id: crypto.randomUUID(), title: '', status: 'Hidden', lessons: [] });

const moveItems = (array: AdminChapter[] | AdminLesson[], from: number, to: number) => {
  const newArr = [...array];
  const item = newArr.splice(from, 1)[0];
  newArr.splice(to, 0, item);

  return newArr;
};
